import { useMemo } from 'react';
import { ArpeggioSelection, getDiatonicArpeggioOffsets } from '../data/arpeggios';
import { CHORD_INTERVALS } from '../data/chords';
import { INTERVAL_BY_LABEL } from '../data/intervals';
import { SCALES, ScaleId } from '../data/scales';
import { SHARP_KEYS } from '../data/notes';
import { transposeNote } from '../utils/musicTheory';

export interface ArpeggioSemitonesResult {
  arpeggioRootNote: string | null;
  semitoneSet: Set<number>;
}

const empty = (): ArpeggioSemitonesResult => ({
  arpeggioRootNote: null,
  semitoneSet: new Set(),
});

export function useArpeggioSemitones(
  selection: ArpeggioSelection,
  scaleRootNote: string | null,
  scaleId: ScaleId | null,
): ArpeggioSemitonesResult {
  return useMemo<ArpeggioSemitonesResult>(() => {
    if (!selection) return empty();

    if (selection.mode === 'free') {
      const labels = CHORD_INTERVALS[selection.quality];
      const semitoneSet = new Set(labels.map(l => INTERVAL_BY_LABEL[l].semitones));
      return { arpeggioRootNote: selection.root, semitoneSet };
    }

    // Diatonic mode
    if (!scaleRootNote || !scaleId) return empty();
    const scale = SCALES[scaleId]?.intervals;
    if (!scale) return empty();

    const result = getDiatonicArpeggioOffsets(scale, selection.degree, selection.size);
    if (!result) return empty();

    const sharps = SHARP_KEYS.has(scaleRootNote);
    const arpeggioRootNote = transposeNote(scaleRootNote, result.degreeRootSemi, sharps);
    return { arpeggioRootNote, semitoneSet: result.offsets };
  }, [selection, scaleRootNote, scaleId]);
}
